import { Formik, Form } from 'formik';
import type { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { Heading, TextLink } from './Typography';
import { MessageCard } from './MessageCard';
import { ResendTimer } from '@lendica/components';
import { PasswordField } from './Fields';
import { useAppContainerContext } from './AppContainer';
import { forgotPassword, passwordCreateOrReset } from '../api';
import { matchHostname } from '@lendica/utils';

export type PasswordFormValues = {
    password: string;
    confirmPassword: string;
};

export type ResetPasswordSubmitHandler = (
    values: PasswordFormValues,
    formikHelpers: FormikHelpers<PasswordFormValues>
) => void | Promise<void>;

export const ResetPassword = ({
    heading = 'Reset your password',
    body = 'Reset the password for your account 🔐.',
    action = 'Reset password',
    onSuccess,
}: {
    heading?: string;
    body?: string;
    action?: string;
    onSuccess?: () => void;
}) => {
    const { loginValue, updateLoginValue, setError, setSuccessMsg, setPageLoading } =
        useAppContainerContext();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loginValue.verifyToken) {
            navigate('/');
        }
    }, [loginValue.verifyToken]);

    const handleSubmit: ResetPasswordSubmitHandler = async (values, { setSubmitting }) => {
        try {
            setSubmitting(true);
            const data = await passwordCreateOrReset(
                values.password,
                values.confirmPassword,
                loginValue.verifyToken
            );
            updateLoginValue({ verifyToken: undefined });
            setSuccessMsg('Password set successfully. Redirecting to login...');
            setPageLoading(true);
            setTimeout(() => {
                setSuccessMsg(null);
                onSuccess?.();
                setSubmitting(false);
                setPageLoading(false);
            }, 1500);
        } catch (error: any) {
            setError(error.message || 'Something went wrong. Please try again.');
            setSubmitting(false);
        }
    };
    return (
        <ResetPasswordComponent
            heading={heading}
            body={body}
            action={action}
            handleSubmit={handleSubmit}
        />
    );
};

export const ResetPasswordComponent = ({
    heading,
    body,
    action,
    handleSubmit,
}: {
    heading: string;
    body: string;
    action: string;
    handleSubmit: ResetPasswordSubmitHandler;
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>{heading}</Heading>
            <MessageCard>{body}</MessageCard>
            <Formik<PasswordFormValues>
                initialValues={{ password: '', confirmPassword: '' }}
                onSubmit={handleSubmit}
                validationSchema={Yup.object({
                    password: Yup.string().min(8, 'At least 8 characters').required('Required'),
                    confirmPassword: Yup.string()
                        .min(8, 'At least 8 characters')
                        .oneOf([Yup.ref('password')], 'Passwords must match')
                        .required('Required'),
                })}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Box
                        component={Form}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}
                    >
                        <Stack sx={{ width: '100%' }}>
                            <PasswordField
                                name="password"
                                label="Password"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!errors.password && touched.password}
                                helperText={errors.password || ' '}
                            />
                            <PasswordField
                                name="confirmPassword"
                                label="Re-enter password"
                                value={values.confirmPassword}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!errors.confirmPassword && touched.confirmPassword}
                                helperText={errors.confirmPassword || ' '}
                            />
                        </Stack>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            fullWidth
                            size="large"
                        >
                            {action}
                        </LoadingButton>
                    </Box>
                )}
            </Formik>
        </Box>
    );
};

export const EmailResetInstructions = () => {
    const { loginValue, setMsg, setError } = useAppContainerContext();
    const navigate = useNavigate();
    const handleResend = async () => {
        try {
            setMsg('Sending email instructions...');
            setError(null);
            const data = await forgotPassword(loginValue.email || '');
            setMsg('Email instructions sent again.');
            setTimeout(() => {
                setMsg(null);
            }, 2500);
        } catch (error) {
            setError('Failed to send reset email. Please try again.');
            setTimeout(() => {
                setError(null);
            }, 2500);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>Follow instructions in email</Heading>
            <MessageCard>
                If a matching account was found, you’ll receive an email with instructions ✉️.
            </MessageCard>
            <Stack sx={{ width: '100%', gap: 4 }}>
                <Stack sx={{ width: '100%' }}>
                    {loginValue.email && (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <TextField
                                name="email"
                                label="Work email"
                                value={loginValue.email}
                                disabled
                                fullWidth
                                helperText={' '}
                            />
                            <ResendTimer seconds={30} onResend={handleResend}>
                                Resend instructions
                            </ResendTimer>
                        </Box>
                    )}
                </Stack>
                <Button variant="contained" fullWidth size="large" onClick={() => navigate('/')}>
                    Return to sign in
                </Button>
            </Stack>
            <Stack sx={{ width: '100%', justifyContent: 'center' }}>
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                    Don't have an account?{' '}
                    <TextLink
                        onClick={() =>
                            window.open(`https://apply.${matchHostname()}/login`, '_blank')
                        }
                    >
                        Sign up
                    </TextLink>
                </Typography>
            </Stack>
        </Box>
    );
};
